import { ContentCopy } from '@mui/icons-material';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { formatCurrencyInCents, presentUuid } from '../../helpers/formatters';
import {
    formatMaintenanceDurationLiteral,
    MAINTENANCE_DOWNTIME_SUFFIX,
} from '../../helpers/printersOverview';
import {
    maintenanceCountLabel,
    replacementPartsCountLabel,
} from '../../helpers/printerMaintenances';

const sectionTitleSx = {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: 'text.secondary',
    mb: 0.5,
};

const labelSx = {
    fontSize: '0.75rem',
    color: 'text.secondary',
    lineHeight: 1.35,
    flexShrink: 0,
    minWidth: 78,
};

const valueSx = {
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.35,
};

function DetailRow({ label, children }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.2 }}>
            <Typography component="span" sx={labelSx}>
                {label}
            </Typography>
            <Box sx={{ ...valueSx, minWidth: 0, flex: 1 }}>{children}</Box>
        </Box>
    );
}

export default function PrinterOverviewDetailsTables({
    printer,
    onCopyUuid,
    onOpenMaintenances,
}) {
    const downtimeMinutes = Number(printer.maintenance_downtime_minutes) || 0;
    const printerId = printer.printer_uuid || printer.id;
    const brandModel = [printer.brand, printer.model]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' - ') || '—';
    const maintenanceCount = Number(printer.maintenance_count) || 0;
    const partsCount = Number(printer.parts_count) || 0;
    const partsTotalCents = Number(printer.parts_total_cents) || 0;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box>
                <Typography
                    sx={{
                        ...sectionTitleSx,
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={onOpenMaintenances}
                >
                    Manutenções
                </Typography>
                <Typography sx={{ ...valueSx, display: 'block' }}>
                    {maintenanceCountLabel(maintenanceCount)}
                </Typography>
                <Typography sx={{ ...valueSx, display: 'block', fontWeight: 500, color: 'text.secondary' }}>
                    {downtimeMinutes > 0
                        ? `${formatMaintenanceDurationLiteral(downtimeMinutes)}${MAINTENANCE_DOWNTIME_SUFFIX}`
                        : 'Sem tempo em manutenção'}
                </Typography>
                <Typography sx={{ ...valueSx, display: 'block', fontWeight: 500, color: 'text.secondary' }}>
                    {replacementPartsCountLabel(partsCount)} ({formatCurrencyInCents(partsTotalCents)})
                </Typography>
            </Box>

            <Box>
                <Typography sx={sectionTitleSx}>Sobre</Typography>
                <DetailRow label="Modelo">{brandModel}</DetailRow>
                <DetailRow label="Consumo">
                    Médio {printer.avg_consumption_watts ?? '—'}W · Máx. {printer.consumption ?? '—'}W
                </DetailRow>
                <DetailRow label="Valor pago">
                    {formatCurrencyInCents(printer.price || 0)}
                </DetailRow>
                <DetailRow label="ID">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                        <Typography
                            component="span"
                            sx={{ ...valueSx, fontFamily: 'monospace' }}
                        >
                            {presentUuid(printerId)}
                        </Typography>
                        <Tooltip title="Copiar UUID completo para o Farm Link">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onCopyUuid?.(printerId)}
                                aria-label="Copiar UUID"
                                sx={{ p: 0.25 }}
                            >
                                <ContentCopy sx={{ fontSize: 12 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </DetailRow>
            </Box>
        </Box>
    );
}
